// Shared helpers for the dashboard tour: locating anchored elements and
// computing the spotlight cutout + tooltip placement. Kept framework-agnostic
// so the overlay component and the tour hook can both reuse them.

export const isMobileViewport = () => window.innerWidth < 1024;

// Single source of truth for the spotlight inset. The dim cutout, the primary
// ring, the ping, and the secondary ring all derive from this so their edges
// stay concentric on tight targets.
export const SPOTLIGHT_PADDING = 8;

/** Space reserved at the viewport bottom for the fixed tour hot bar. */
export const TOUR_HOT_BAR_CLEARANCE = 100;

/** Lift page chrome (e.g. chat compose) so a bottom target clears the hot bar. */
export function applyTourBottomLift(pixels) {
  if (typeof document === 'undefined') return;
  const px = Math.max(0, Math.ceil(pixels || 0));
  if (px <= 0) {
    document.documentElement.style.removeProperty('--playbook-bottom-lift');
    return;
  }
  document.documentElement.style.setProperty('--playbook-bottom-lift', `${px}px`);
}

export function clearTourBottomLift() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty('--playbook-bottom-lift');
}

/** How many pixels a rect sticks into the hot-bar zone (0 if clear). */
export function getHotBarOverlap(rect, clearance = TOUR_HOT_BAR_CLEARANCE) {
  if (!rect || typeof window === 'undefined') return 0;
  const clearLine = window.innerHeight - clearance;
  return Math.max(0, rect.bottom - clearLine);
}

/** Table-intel only: fit the full client row on screen (no fancy scroll math). */
export function showAdminTableFullRow() {
  if (typeof document === 'undefined') return;
  const scroller = document.querySelector('[data-tour-scroll="playbook-admin-table"]');
  if (!scroller) return;
  scroller.classList.add('playbook-tour-full-row');
  scroller.scrollLeft = 0;
}

export function clearAdminTableTourLayout() {
  if (typeof document === 'undefined') return;
  const scroller = document.querySelector('[data-tour-scroll="playbook-admin-table"]');
  if (!scroller) return;
  scroller.classList.remove('playbook-tour-full-row');
  scroller.classList.remove('playbook-tour-unstick');
}

export const ADMIN_DASHBOARD_READY_SELECTOR = '[data-playbook-dashboard-ready="true"]';

export function isAdminDashboardReady() {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector(ADMIN_DASHBOARD_READY_SELECTOR);
}

export const TOPBAR_CLIENT_MENU_CLOSE_EVENT = 'portfolio-playbook-close-client-menu';
export const TOPBAR_CLIENT_PICKED_EVENT = 'portfolio-playbook-client-picked';
export const TOPBAR_NAV_MENU_LOCK_EVENT = 'portfolio-playbook-lock-nav-menu';
export const TOPBAR_NAV_MENU_UNLOCK_EVENT = 'portfolio-playbook-unlock-nav-menu';
/** Force-open a top-bar nav dropdown. detail.kind: 'company' | 'plans' */
export const TOPBAR_NAV_MENU_OPEN_EVENT = 'portfolio-playbook-open-nav-menu';

// Keep a top-bar nav dropdown (e.g. Plans) open during the tour: while locked,
// hover open/close is suppressed so a click behaves like the client menu.
export function lockTopbarNavMenu() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOPBAR_NAV_MENU_LOCK_EVENT));
}

export function unlockTopbarNavMenu() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOPBAR_NAV_MENU_UNLOCK_EVENT));
}

/** Open Company or Plans without toggling (safe for tour prepare). */
export function openTopbarNavMenu(kind) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOPBAR_NAV_MENU_OPEN_EVENT, { detail: { kind } }));
}

export function closeTopbarClientMenu() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TOPBAR_CLIENT_MENU_CLOSE_EVENT));
}

const isVisible = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.visibility !== 'hidden' &&
    style.display !== 'none' &&
    style.opacity !== '0'
  );
};

const getLayoutSection = (el) => {
  let parent = el;
  while (parent && parent !== document.body) {
    const cls = typeof parent.className === 'string' ? parent.className : '';
    if (/\b(sm|md|lg|xl):hidden\b/.test(cls)) return 'mobile';
    if (/\bhidden\b/.test(cls) && /\b(sm|md|lg|xl):(block|flex|grid|table)\b/.test(cls)) return 'desktop';
    parent = parent.parentElement;
  }
  return 'neutral';
};

const isInMobileSection = (el) => getLayoutSection(el) === 'mobile';

// Resolve a `data-tour` target to the best matching visible element. Desktop and
// mobile layouts frequently render duplicate anchors, so prefer the one that
// belongs to the active layout (mobile menu vs. desktop bar).
export function findTourElement(target, { requiresMenu = false, isMobile = false } = {}) {
  const elements = findTourTargetElements(target, { requiresMenu, isMobile });
  return elements[0] || null;
}

export function findTourTargetElements(target, { requiresMenu = false, isMobile = false } = {}) {
  const all = Array.from(document.querySelectorAll(`[data-tour="${target}"]`));
  if (all.length === 0) return [];

  const wantsMobile = isMobile || requiresMenu;
  const preferred = all.filter((el) => {
    if (!isVisible(el)) return false;
    const section = getLayoutSection(el);
    if (wantsMobile) return section !== 'desktop';
    return section !== 'mobile';
  });
  if (preferred.length) return preferred;

  const anyVisible = all.filter(isVisible);
  return anyVisible.length ? anyVisible : [all[0]];
}

export function getUnionRect(elements) {
  const rects = elements
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
  if (!rects.length) return null;

  const top = Math.min(...rects.map((r) => r.top));
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { top, left, right, bottom, width: right - left, height: bottom - top };
}

export function findTourHighlight(target, { requiresMenu = false, isMobile = false, union = false } = {}) {
  const elements = findTourTargetElements(target, { requiresMenu, isMobile });
  if (!elements.length) return null;

  const useUnion = union || elements.length > 1;
  if (useUnion) {
    const rect = getUnionRect(elements);
    if (!rect) return null;
    return { elements, rect, primary: elements[0] };
  }

  const primary = elements[0];
  const rect = primary.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return { elements: [primary], rect, primary };
}

export function isPointInRect(x, y, rect) {
  if (!rect) return false;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

// Table rows: union cell rects clipped to the table scroll viewport (not full window width).
export function getElementHighlightRect(element) {
  if (!element) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Rows: clip to the table scrollport so the glow matches visible cells only.
  if (element.tagName === 'TR') {
    const scrollContainer = element.closest('[data-tour-scroll]');
    const scrollRect = scrollContainer?.getBoundingClientRect();
    const clip = scrollRect
      ? {
          top: Math.max(0, scrollRect.top),
          bottom: Math.min(vh, scrollRect.bottom),
          left: Math.max(0, scrollRect.left),
          right: Math.min(vw, scrollRect.right),
        }
      : { top: 0, bottom: vh, left: 0, right: vw };

    const intersect = (r) => {
      const left = Math.max(clip.left, r.left);
      const right = Math.min(clip.right, r.right);
      const top = Math.max(clip.top, r.top);
      const bottom = Math.min(clip.bottom, r.bottom);
      if (right <= left || bottom <= top) return null;
      return { top, left, right, bottom };
    };

    const cells = Array.from(element.cells || element.querySelectorAll('td'));
    const rects = cells
      .map((cell) => intersect(cell.getBoundingClientRect()))
      .filter(Boolean);

    if (!rects.length) {
      const fallback = intersect(element.getBoundingClientRect());
      if (!fallback) return null;
      return {
        ...fallback,
        width: fallback.right - fallback.left,
        height: fallback.bottom - fallback.top,
      };
    }

    const top = Math.min(...rects.map((r) => r.top));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    const left = Math.min(...rects.map((r) => r.left));
    const right = Math.max(...rects.map((r) => r.right));
    return {
      top,
      left,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }

  // Buttons / cards: use the raw layout box (no viewport clip) so full-width
  // bars like Personal Details keep a full ring before/while scrolling into view.
  // Overlay menus (absolute/fixed client list) that spill off-screen are clipped
  // so the spotlight never "escapes" above the viewport.
  const r = element.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  const position = window.getComputedStyle(element).position;
  const isOverlay = position === 'fixed' || position === 'absolute';
  const spills =
    r.top < -1 || r.left < -1 || r.bottom > vh + 1 || r.right > vw + 1;
  if (isOverlay && spills) {
    const top = Math.max(0, r.top);
    const left = Math.max(0, r.left);
    const right = Math.min(vw, r.right);
    const bottom = Math.min(vh, r.bottom);
    if (right <= left || bottom <= top) return null;
    return {
      top,
      left,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }
  return {
    top: r.top,
    left: r.left,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  };
}

// Four rectangles that dim everything except a padded box around the target,
// producing a spotlight. Falls back to a full-screen dim when no target.
export function getSpotlightRegions(rect, padding = SPOTLIGHT_PADDING) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fullScreen = [{ top: 0, left: 0, width: '100%', height: '100%' }];
  if (!rect) return fullScreen;

  const regions = [];
  if (rect.top > padding) {
    regions.push({ top: 0, left: 0, width: '100%', height: `${Math.max(0, rect.top - padding)}px` });
  }
  if (rect.bottom + padding < vh) {
    regions.push({
      top: `${rect.bottom + padding}px`,
      left: 0,
      width: '100%',
      height: `${Math.max(0, vh - rect.bottom - padding)}px`,
    });
  }
  if (rect.left > padding) {
    regions.push({
      top: `${Math.max(0, rect.top - padding)}px`,
      left: 0,
      width: `${Math.max(0, rect.left - padding)}px`,
      height: `${rect.height + padding * 2}px`,
    });
  }
  if (rect.right + padding < vw) {
    regions.push({
      top: `${Math.max(0, rect.top - padding)}px`,
      left: `${rect.right + padding}px`,
      width: `${Math.max(0, vw - rect.right - padding)}px`,
      height: `${rect.height + padding * 2}px`,
    });
  }
  return regions.length ? regions : fullScreen;
}

// Fixed top-center bar for spotlight steps — stays visible above page content.
export function getTopCenteredPanelPlacement({ padding = 12, maxWidth = 560 } = {}) {
  return {
    layout: 'top-center',
    style: {
      top: `${padding}px`,
      left: '50%',
      transform: 'translateX(-50%)',
      width: `min(92vw, ${maxWidth}px)`,
    },
  };
}

// Fixed bottom-center bar — for highlights near the top of the page.
// Always clear the tour hot bar so the card is never covered.
export function getBottomCenteredPanelPlacement({ padding = 12, maxWidth = 560 } = {}) {
  const bottom = Math.max(padding, TOUR_HOT_BAR_CLEARANCE);
  return {
    layout: 'bottom-center',
    style: {
      bottom: `${bottom}px`,
      left: '50%',
      transform: 'translateX(-50%)',
      width: `min(92vw, ${maxWidth}px)`,
    },
  };
}

// Place the tour card outside the spotlight when possible (e.g. chat thread above compose).
export function getSpotlightPanelPlacement(
  rect,
  { preferred = 'top', panelHeight = 200, padding = 12, maxWidth = 560, spacing = 10 } = {}
) {
  if (!rect) {
    return preferred === 'bottom'
      ? getBottomCenteredPanelPlacement({ padding, maxWidth })
      : getTopCenteredPanelPlacement({ padding, maxWidth });
  }

  const vh = window.innerHeight;
  const bottomPad = Math.max(padding, TOUR_HOT_BAR_CLEARANCE);
  const roomBelow = vh - rect.bottom - bottomPad;
  const roomAbove = rect.top - padding;
  const centeredX = {
    left: '50%',
    transform: 'translateX(-50%)',
    width: `min(92vw, ${maxWidth}px)`,
  };

  const belowHighlight = () => ({
    layout: 'below-highlight',
    blend: true,
    style: {
      ...centeredX,
      top: `${rect.bottom + spacing}px`,
      maxHeight: `calc(100vh - ${rect.bottom + spacing}px - ${bottomPad}px)`,
    },
  });

  // Scrolling is disabled, so the card must always render at its full natural
  // height. When it can't fully clear the highlight, pin it to whichever
  // viewport edge has more free room. The card is short, so this keeps it
  // fully visible; overlapping the dimmed highlight is acceptable, clipping the
  // card is not.
  const edgePinned = () =>
    roomAbove >= roomBelow
      ? getTopCenteredPanelPlacement({ padding, maxWidth })
      : getBottomCenteredPanelPlacement({ padding, maxWidth });

  if (preferred === 'bottom') {
    if (roomBelow >= panelHeight + spacing) return belowHighlight();
    if (roomAbove >= panelHeight + spacing) return getTopCenteredPanelPlacement({ padding, maxWidth });
    return edgePinned();
  }

  if (roomAbove >= panelHeight + spacing) return getTopCenteredPanelPlacement({ padding, maxWidth });
  if (roomBelow >= panelHeight + spacing) return belowHighlight();
  return edgePinned();
}

// Dock the tour card beside an anchor (e.g. empty-chat copy), not over it.
export function getAdjacentPanelPlacement(
  anchorRect,
  side = 'left',
  { panelHeight = 220, padding = 12, spacing = 16, maxWidth = 400 } = {}
) {
  if (!anchorRect) return getTopCenteredPanelPlacement({ padding, maxWidth });

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bottomPad = Math.max(padding, TOUR_HOT_BAR_CLEARANCE);
  const width = Math.min(maxWidth, Math.max(260, vw * 0.36));
  const centerY = anchorRect.top + anchorRect.height / 2;
  const top = Math.min(
    Math.max(padding, centerY - panelHeight / 2),
    vh - panelHeight - bottomPad
  );
  const maxHeight = `calc(100vh - ${top}px - ${bottomPad}px)`;

  const placeLeft = () => {
    const left = anchorRect.left - width - spacing;
    if (left >= padding) {
      return {
        layout: 'adjacent-left',
        blend: true,
        style: { top: `${top}px`, left: `${left}px`, width: `${width}px`, maxHeight },
      };
    }
    return null;
  };

  const placeRight = () => {
    const left = anchorRect.right + spacing;
    if (left + width <= vw - padding) {
      return {
        layout: 'adjacent-right',
        blend: true,
        style: { top: `${top}px`, left: `${left}px`, width: `${width}px`, maxHeight },
      };
    }
    return null;
  };

  // When neither side fits (narrow / mobile), dock on the opposite end of the
  // viewport from the anchor so the card never overlaps a top- or bottom-edge
  // highlight (e.g. the top-bar client dropdown).
  const stackedFallback = () =>
    centerY < vh / 2
      ? getBottomCenteredPanelPlacement({ padding, maxWidth: width })
      : getTopCenteredPanelPlacement({ padding, maxWidth: width });

  if (side === 'left') {
    return placeLeft() || placeRight() || stackedFallback();
  }
  return placeRight() || placeLeft() || stackedFallback();
}

// Leave room for a fixed tour card so the user can still scroll the highlight.
export function scrollTourTargetIntoView(element, { panelEdge = 'top', panelSize = 220, padding = 12 } = {}) {
  if (!element) return;
  const rect = element.getBoundingClientRect();
  const absTop = window.scrollY + rect.top;
  const absBottom = window.scrollY + rect.bottom;
  const vh = window.innerHeight;

  if (panelEdge === 'top') {
    const top = Math.max(0, absTop - panelSize - padding);
    window.scrollTo({ top, behavior: 'smooth' });
    return;
  }

  const visibleBottom = window.scrollY + vh - panelSize - padding;
  if (absBottom > visibleBottom) {
    window.scrollTo({ top: Math.max(0, absBottom - vh + panelSize + padding), behavior: 'smooth' });
  } else if (absTop < window.scrollY + padding) {
    window.scrollTo({ top: Math.max(0, absTop - padding), behavior: 'smooth' });
  }
}

// Place spotlight label above or below the ring so it never covers the control.
export function getSpotlightLabelStyle(rect, { placement, isRtl = false } = {}) {
  if (!rect) return null;
  const centerX = rect.left + rect.width / 2;
  const nearTop = rect.top < 88;
  const nearBottom = rect.bottom > window.innerHeight - 88;
  let edge = placement;
  if (!edge) {
    if (nearTop) edge = 'bottom';
    else if (nearBottom) edge = 'top';
    else edge = 'top';
  }

  if (edge === 'bottom') {
    return {
      top: `${rect.bottom + 10}px`,
      left: `${centerX}px`,
      transform: 'translateX(-50%)',
    };
  }

  return {
    top: `${Math.max(8, rect.top - 40)}px`,
    left: `${centerX}px`,
    transform: 'translateX(-50%)',
  };
}

// Place a fixed side rail for portrait mascot clips — opposite the highlight when possible.
export function getSidePanelPlacement(rect, { isRtl = false, panelWidth = 288, padding = 12 } = {}) {
  const vw = window.innerWidth;
  const minSideWidth = 520;

  if (vw < minSideWidth) {
    return { ...getDockedBarPlacement(rect, { dockHeight: 200, padding }), layout: 'bottom' };
  }

  const leadingKey = isRtl ? 'right' : 'left';
  const trailingKey = isRtl ? 'left' : 'right';
  const width = Math.min(panelWidth, vw - padding * 2);

  let edgeKey = trailingKey;
  if (rect) {
    const highlightCenterX = rect.left + rect.width / 2;
    const onTrailingHalf = isRtl ? highlightCenterX < vw / 2 : highlightCenterX > vw / 2;
    edgeKey = onTrailingHalf ? leadingKey : trailingKey;
  }

  return {
    layout: 'side',
    edge: edgeKey,
    style: {
      top: '50%',
      transform: 'translateY(-50%)',
      width: `${width}px`,
      [edgeKey]: `${padding}px`,
      maxHeight: `calc(100vh - ${padding}px - ${Math.max(padding, TOUR_HOT_BAR_CLEARANCE)}px)`,
    },
  };
}

// Place a fixed dock bar away from the highlighted target so the card never covers it.
export function getDockedBarPlacement(rect, { dockHeight = 152, padding = 12 } = {}) {
  const vh = window.innerHeight;
  if (!rect) {
    return {
      edge: 'bottom',
      style: { bottom: `${padding}px`, left: `${padding}px`, right: `${padding}px` },
    };
  }

  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const needsTop = spaceBelow < dockHeight + padding + 24 && spaceAbove > dockHeight + padding + 24;

  if (needsTop) {
    return {
      edge: 'top',
      style: { top: `${padding}px`, left: `${padding}px`, right: `${padding}px` },
    };
  }

  return {
    edge: 'bottom',
    style: { bottom: `${padding}px`, left: `${padding}px`, right: `${padding}px` },
  };
}

// Place the tooltip relative to the target, flipping to the opposite side when
// the preferred side would overflow the viewport. Tall targets (e.g. a full-height
// chat thread) are treated as viewport-centered so the card stays clickable.
export function getTooltipPosition(
  rect,
  position = 'bottom',
  { tooltipWidth = 400, tooltipHeight = 320, spacing = 16, padding = 16 } = {}
) {
  const centered = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (!rect) return centered;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const clampX = (x) =>
    Math.min(Math.max(x, tooltipWidth / 2 + padding), vw - tooltipWidth / 2 - padding);

  // Targets taller than ~45% of the viewport can't be edge-anchored reliably.
  const isOversizedTarget = rect.height > vh * 0.45;
  if (isOversizedTarget) {
    return { top: `${vh / 2}px`, left: `${clampX(centerX)}px`, transform: 'translate(-50%, -50%)' };
  }

  const clampY = (y) =>
    Math.min(Math.max(y, tooltipHeight / 2 + padding), vh - tooltipHeight / 2 - padding);

  const viewportCentered = () => ({
    top: `${clampY(vh / 2)}px`,
    left: `${clampX(centerX)}px`,
    transform: 'translate(-50%, -50%)',
  });

  switch (position) {
    case 'top':
      if (rect.top - tooltipHeight - spacing >= padding) {
        return { top: `${rect.top - spacing}px`, left: `${clampX(centerX)}px`, transform: 'translate(-50%, -100%)' };
      }
      if (rect.bottom + tooltipHeight + spacing < vh - padding) {
        return { top: `${rect.bottom + spacing}px`, left: `${clampX(centerX)}px`, transform: 'translate(-50%, 0)' };
      }
      return viewportCentered();

    case 'right':
      if (rect.right + tooltipWidth + spacing < vw - padding) {
        return { top: `${clampY(centerY)}px`, left: `${rect.right + spacing}px`, transform: 'translate(0, -50%)' };
      }
      if (rect.left - tooltipWidth - spacing > padding) {
        return { top: `${clampY(centerY)}px`, left: `${rect.left - spacing}px`, transform: 'translate(-100%, -50%)' };
      }
      return viewportCentered();

    case 'left':
      if (rect.left - tooltipWidth - spacing > padding) {
        return { top: `${clampY(centerY)}px`, left: `${rect.left - spacing}px`, transform: 'translate(-100%, -50%)' };
      }
      if (rect.right + tooltipWidth + spacing < vw - padding) {
        return { top: `${clampY(centerY)}px`, left: `${rect.right + spacing}px`, transform: 'translate(0, -50%)' };
      }
      return viewportCentered();

    case 'bottom':
    default:
      if (rect.bottom + tooltipHeight + spacing < vh - padding) {
        return { top: `${rect.bottom + spacing}px`, left: `${clampX(centerX)}px`, transform: 'translate(-50%, 0)' };
      }
      if (rect.top - tooltipHeight - spacing >= padding) {
        return { top: `${rect.top - spacing}px`, left: `${clampX(centerX)}px`, transform: 'translate(-50%, -100%)' };
      }
      return viewportCentered();
  }
}
